import {
    AlertCircle,
    ArrowRightLeft,
    Calculator,
    Calendar,
    CheckCircle,
    ChevronDown,
    ChevronRight,
    Download,
    Link as LinkIcon,
    Loader2,
    Map as MapIcon,
    Plus,
    RefreshCw,
    RotateCcw,
    Save,
    Trash2,
    Wifi,
    WifiOff,
    X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { settingsService } from '../services/settingsService';
import { tripService } from '../services/tripService';
import type { Trip } from '../types';
import { TripType } from '../types';
import AddressAutocomplete from './AddressAutocomplete';

// ── Types ─────────────────────────────────────────────────────────────────────

interface ProposedTrip {
  id: string;          // Google Calendar event ID (or synthetic 'return-home-YYYY-MM-DD')
  dbTripId?: string;   // Azure database trip ID, if already saved
  date: string;        // YYYY-MM-DD
  dateTime: string;    // ISO datetime (event start)
  endDateTime?: string;
  purpose: string;
  startLocation: string;
  endLocation: string;
  distanceKm: number | null;
  status: 'pending' | 'calculating' | 'success' | 'error';
  isAlreadySaved: boolean;
  errorMessage?: string;
  columnOverride?: 'with' | 'without' | null;
}

interface CalendarImportProps {
  onImport: () => void;
  selectedMonth: number; // 0-indexed
  selectedYear: number;
}

interface GoogleCalendarOption {
  id: string;
  summary: string;
  accessRole: string;
  primary?: boolean;
}

const GOOGLE_TOKEN_KEY = 'google_calendar_token';
const GOOGLE_SELECTED_CALENDAR_KEY = 'google_calendar_selected_id';
const MONTH_NAMES = [
  'Januar', 'Februar', 'Marts', 'April', 'Maj', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'December',
];

// ── Component ─────────────────────────────────────────────────────────────────

export default function CalendarImport({ onImport, selectedMonth, selectedYear }: CalendarImportProps) {
  const [withLocCollapsed, setWithLocCollapsed] = useState(false);
  const [withoutLocCollapsed, setWithoutLocCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [accessToken, setAccessToken] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'checking' | 'valid' | 'expired' | 'disconnected'>('disconnected');
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [fetchMode, setFetchMode] = useState<'month' | 'year'>('month');
  const [fetchedEventCount, setFetchedEventCount] = useState<number | null>(null);
  const [fetchingPage, setFetchingPage] = useState(0);
  const [availableCalendars, setAvailableCalendars] = useState<GoogleCalendarOption[]>([]);
  const [selectedCalendarId, setSelectedCalendarId] = useState('');
  const [loadingCalendars, setLoadingCalendars] = useState(false);

  const [proposedTrips, setProposedTrips] = useState<ProposedTrip[]>([]);
  const [selectedTrips, setSelectedTrips] = useState<string[]>([]);
  const [calculatingTripId, setCalculatingTripId] = useState<string | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [homeAddress, setHomeAddress] = useState('');
  const [orphanedTripCount, setOrphanedTripCount] = useState(0);

  const [showNewEventForm, setShowNewEventForm] = useState(false);
  const [savingEvent, setSavingEvent] = useState(false);
  const [newEvent, setNewEvent] = useState({
    summary: '',
    location: '',
    date: new Date().toISOString().split('T')[0]!,
    startTime: '08:00',
    endTime: '09:00',
    isBusiness: true,
    description: '',
  });

  useEffect(() => {
    checkGoogleConnection();
    loadHomeAddress();
  }, []);

  // Re-validate token every 5 minutes while connected
  useEffect(() => {
    if (!isConnected || !accessToken) return;
    const interval = setInterval(() => validateToken(accessToken), 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [isConnected, accessToken]);

  // ── Settings ─────────────────────────────────────────────────────────────────

  const loadHomeAddress = async () => {
    try {
      const settings = await settingsService.getSettings();
      setHomeAddress(settings?.homeAddress ?? '');
    } catch {
      // ignore — settings may not exist yet
    }
  };

  const loadUserCalendars = async (token: string) => {
    setLoadingCalendars(true);
    try {
      // Only keep calendars the user owns to match import requirements.
      const response = await fetch(
        'https://www.googleapis.com/calendar/v3/users/me/calendarList?minAccessRole=owner&showDeleted=false&showHidden=false&maxResults=250&fields=items(id,summary,primary,accessRole)',
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.ok) {
        throw new Error(`Google Calendar list fejl: ${response.status}`);
      }

      const payload = await response.json();
      const calendars = ((payload.items ?? []) as GoogleCalendarOption[])
        .filter((item) => item.accessRole === 'owner')
        .sort((a, b) => {
          if (a.primary) return -1;
          if (b.primary) return 1;
          return a.summary.localeCompare(b.summary, 'da-DK');
        });

      setAvailableCalendars(calendars);

      const savedCalendarId = localStorage.getItem(GOOGLE_SELECTED_CALENDAR_KEY);
      const fallback = calendars.find((c) => c.primary)?.id ?? calendars[0]?.id ?? '';
      const nextSelected =
        savedCalendarId && calendars.some((c) => c.id === savedCalendarId)
          ? savedCalendarId
          : fallback;

      setSelectedCalendarId(nextSelected);
      if (nextSelected) {
        localStorage.setItem(GOOGLE_SELECTED_CALENDAR_KEY, nextSelected);
      } else {
        localStorage.removeItem(GOOGLE_SELECTED_CALENDAR_KEY);
      }
    } catch (err) {
      console.error('Fejl ved hentning af kalendere:', err);
      setAvailableCalendars([]);
      setSelectedCalendarId('');
    } finally {
      setLoadingCalendars(false);
    }
  };

  // ── Google OAuth ──────────────────────────────────────────────────────────────

  /** Lightweight token validation via a cheap Google Calendar API call. */
  const validateToken = async (token: string): Promise<boolean> => {
    setConnectionStatus('checking');
    try {
      const res = await fetch(
        'https://www.googleapis.com/calendar/v3/calendars/primary?fields=id',
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        setConnectionStatus('valid');
        setLastChecked(new Date());
        return true;
      }
      setConnectionStatus('expired');
      setAvailableCalendars([]);
      setSelectedCalendarId('');
      return false;
    } catch {
      setConnectionStatus('expired');
      setAvailableCalendars([]);
      setSelectedCalendarId('');
      return false;
    }
  };

  const checkGoogleConnection = async () => {
    setConnectionStatus('checking');
    const token = localStorage.getItem(GOOGLE_TOKEN_KEY);
    if (token) {
      setAccessToken(token);
      setIsConnected(true);
      const isValid = await validateToken(token);
      if (isValid) {
        await loadUserCalendars(token);
      }
    } else {
      setIsConnected(false);
      setConnectionStatus('disconnected');
      setAvailableCalendars([]);
      setSelectedCalendarId('');
    }
  };

  const handleConnectCalendar = () => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!clientId) {
      alert('Google Client ID mangler i .env');
      return;
    }
    sessionStorage.setItem('google_oauth_flow', 'true');
    const redirectUri = `${window.location.origin}/oauth-callback.html`;
    const scope = [
      'https://www.googleapis.com/auth/calendar.readonly',
      'https://www.googleapis.com/auth/calendar.events',
    ].join(' ');
    const authUrl =
      `https://accounts.google.com/o/oauth2/v2/auth` +
      `?client_id=${clientId}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&response_type=token` +
      `&scope=${encodeURIComponent(scope)}` +
      `&prompt=consent`;
    window.location.href = authUrl;
  };

  const handleDisconnectCalendar = () => {
    if (!confirm('Er du sikker på at du vil afbryde forbindelsen til Google Kalender?')) return;
    localStorage.removeItem(GOOGLE_TOKEN_KEY);
    localStorage.removeItem(GOOGLE_SELECTED_CALENDAR_KEY);
    setAccessToken('');
    setIsConnected(false);
    setConnectionStatus('disconnected');
    setAvailableCalendars([]);
    setSelectedCalendarId('');
    setProposedTrips([]);
    alert('Google Kalender afbrudt');
  };

  const handleCalendarChange = (calendarId: string) => {
    setSelectedCalendarId(calendarId);
    localStorage.setItem(GOOGLE_SELECTED_CALENDAR_KEY, calendarId);
    setProposedTrips([]);
    setFetchedEventCount(null);
  };

  // ── Period helpers ────────────────────────────────────────────────────────────

  const getYearRange = () => ({
    timeMin: new Date(selectedYear, 0, 1, 0, 0, 0, 0),
    timeMax: new Date(selectedYear, 11, 31, 23, 59, 59, 999),
  });

  /** Returns fiscal-month-adjusted date range, falling back to calendar month. */
  const getPeriodRange = async (): Promise<{ from: string; to: string }> => {
    let timeMin = new Date(selectedYear, selectedMonth, 1);
    let timeMax = new Date(selectedYear, selectedMonth + 1, 0, 23, 59, 59);
    try {
      const fiscalYears = await settingsService.getFiscalYears();
      const fy = fiscalYears.find((y) => y.year === selectedYear);
      if (fy) {
        const months = await settingsService.getFiscalMonths(fy.id);
        const fm = months.find((m) => m.monthNumber === selectedMonth + 1);
        if (fm) {
          timeMin = new Date(fm.startDate + 'T00:00:00');
          timeMax = new Date(fm.endDate + 'T23:59:59.999');
        }
      }
    } catch {
      // fall back to calendar month
    }
    return {
      from: timeMin.toISOString().split('T')[0]!,
      to: timeMax.toISOString().split('T')[0]!,
    };
  };

  const getEffectivePeriodRange = async (): Promise<{ from: string; to: string }> => {
    if (fetchMode === 'year') {
      const { timeMin, timeMax } = getYearRange();
      return {
        from: timeMin.toISOString().split('T')[0]!,
        to: timeMax.toISOString().split('T')[0]!,
      };
    }
    return getPeriodRange();
  };

  const getPeriodLabel = () =>
    fetchMode === 'year'
      ? `hele ${selectedYear}`
      : `${MONTH_NAMES[selectedMonth]} ${selectedYear}`;

  // ── Trip helpers ──────────────────────────────────────────────────────────────

  /** Fetch ALL trips for the user and filter to the given date range. */
  const getAllTripsForPeriod = async (from: string, to: string): Promise<Trip[]> => {
    const result = await tripService.getTrips(undefined, 1, 100000);
    return result.data.filter((t) => t.date >= from && t.date <= to);
  };

  // ── Delete period operations ──────────────────────────────────────────────────

  const deleteCalendarTripsForPeriod = async () => {
    const label = getPeriodLabel();
    if (
      !confirm(
        `Slet ALLE kalenderimporterede ture for ${label}?\n\n` +
          `Dette nulstiller perioden, så du kan starte forfra fra kalenderen. Manuelt oprettede ture berøres ikke.\n\n` +
          `Kan ikke fortrydes.`
      )
    )
      return;
    setIsDeleting(true);
    try {
      const { from, to } = await getEffectivePeriodRange();
      const trips = await getAllTripsForPeriod(from, to);
      const calendarTrips = trips.filter((t) => !!t.googleEventId);
      for (const t of calendarTrips) await tripService.deleteTrip(t.id);
      alert(`${calendarTrips.length} kalenderture slettet for ${label}.\nDu kan nu hente kalenderen forfra.`);
      setProposedTrips([]);
      onImport();
    } catch (err) {
      console.error(err);
      alert('Fejl ved sletning af kalenderture.');
    } finally {
      setIsDeleting(false);
    }
  };

  const deleteManualTripsForPeriod = async () => {
    const label = getPeriodLabel();
    if (
      !confirm(
        `Slet ALLE manuelt oprettede ture for ${label}?\n\n` +
          `Kalenderimporterede ture berøres ikke.\n\nKan ikke fortrydes.`
      )
    )
      return;
    setIsDeleting(true);
    try {
      const { from, to } = await getEffectivePeriodRange();
      const trips = await getAllTripsForPeriod(from, to);
      const manual = trips.filter((t) => !t.googleEventId);
      for (const t of manual) await tripService.deleteTrip(t.id);
      alert(`${manual.length} manuelle ture slettet for ${label}.`);
      onImport();
    } catch (err) {
      console.error(err);
      alert('Fejl ved sletning af manuelle ture.');
    } finally {
      setIsDeleting(false);
    }
  };

  const deleteAllTripsForPeriod = async () => {
    const label = getPeriodLabel();
    setIsDeleting(true);
    try {
      const { from, to } = await getEffectivePeriodRange();
      const trips = await getAllTripsForPeriod(from, to);
      const manualCount = trips.filter((t) => !t.googleEventId).length;
      const manualNote =
        manualCount > 0
          ? `\n\n⚠️ ${manualCount} manuelt oprettede tur${manualCount !== 1 ? 'e' : ''} vil OGSÅ blive slettet.`
          : '';
      if (
        !confirm(
          `NULSTIL ${label}?${manualNote}\n\n` +
            `Dette sletter ALLE ture for perioden. Kan ikke fortrydes.`
        )
      ) {
        setIsDeleting(false);
        return;
      }
      for (const t of trips) await tripService.deleteTrip(t.id);
      alert(`${trips.length} ture slettet for ${label}.\nPerioden er nu tom.`);
      setProposedTrips([]);
      setOrphanedTripCount(0);
      onImport();
    } catch (err) {
      console.error(err);
      alert('Fejl ved nulstilling af periode.');
    } finally {
      setIsDeleting(false);
    }
  };

  // ── Create Google Calendar event ──────────────────────────────────────────────

  const createCalendarEvent = async () => {
    if (!selectedCalendarId) {
      alert('Vaelg venligst en kalender for du opretter aftaler.');
      return;
    }
    if (!newEvent.summary.trim()) {
      alert('Angiv venligst en titel for aftalen.');
      return;
    }
    if (!newEvent.location.trim()) {
      alert('Angiv venligst en lokation/adresse for aftalen.');
      return;
    }
    setSavingEvent(true);
    try {
      const timeZone = 'Europe/Copenhagen';
      const descLines = [
        newEvent.isBusiness ? 'Erhvervskørsel' : 'Privatkørsel',
        newEvent.description,
      ]
        .filter(Boolean)
        .join('\n');
      const body = {
        summary: newEvent.summary,
        location: newEvent.location,
        description: descLines,
        start: { dateTime: `${newEvent.date}T${newEvent.startTime}:00`, timeZone },
        end: { dateTime: `${newEvent.date}T${newEvent.endTime}:00`, timeZone },
      };
      const res = await fetch(
        `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(selectedCalendarId)}/events`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }
      );
      if (!res.ok) {
        if (res.status === 401) {
          alert('Din Google session er udløbet. Tilslut venligst igen.');
          return;
        }
        if (res.status === 403) {
          alert(
            'Adgang nægtet (403). Gennemgå Google-tilslutning igen for at give skrivetilladelse til kalenderen.'
          );
          return;
        }
        const errText = await res.text().catch(() => '');
        throw new Error(`Google Calendar API ${res.status}: ${errText.slice(0, 120)}`);
      }
      setNewEvent((p) => ({
        ...p,
        summary: '',
        location: '',
        startTime: p.endTime,
        endTime: p.endTime,
        description: '',
      }));
      setShowNewEventForm(false);
      // Reload calendar so the new event appears immediately
      await loadCalendarEvents();
    } catch (err) {
      console.error('Fejl ved oprettelse af kalenderaftale:', err);
      alert(`Fejl ved oprettelse: ${err instanceof Error ? err.message : 'Ukendt fejl'}`);
    } finally {
      setSavingEvent(false);
    }
  };

  // ── Google Calendar fetch + trip chain logic ──────────────────────────────────

  const loadCalendarEvents = async () => {
    if (!accessToken) {
      alert('Tilslut venligst Google Kalender først');
      return;
    }
    if (!selectedCalendarId) {
      alert('Vaelg venligst en Google-kalender for at hente aftaler.');
      return;
    }
    if (!homeAddress) {
      alert(
        'Du skal angive din hjemmeadresse under Indstillinger, før du kan hente kalenderaftaler. ' +
          'Hjemmeadressen bruges som startpunkt for den første tur hver dag.'
      );
      return;
    }

    setLoading(true);
    setProposedTrips([]);
    try {
      let timeMin: Date;
      let timeMax: Date;

      if (fetchMode === 'year') {
        const r = getYearRange();
        timeMin = r.timeMin;
        timeMax = r.timeMax;
      } else {
        timeMin = new Date(selectedYear, selectedMonth, 1);
        timeMax = new Date(selectedYear, selectedMonth + 1, 0, 23, 59, 59);
        try {
          const fiscalYears = await settingsService.getFiscalYears();
          const fy = fiscalYears.find((y) => y.year === selectedYear);
          if (fy) {
            const months = await settingsService.getFiscalMonths(fy.id);
            const fm = months.find((m) => m.monthNumber === selectedMonth + 1);
            if (fm) {
              timeMin = new Date(fm.startDate + 'T00:00:00');
              timeMax = new Date(fm.endDate + 'T23:59:59.999');
            }
          }
        } catch {
          // fall back to calendar month
        }
      }

      // Paginated Google Calendar API fetch (max 20 pages x 250 = 5000 events)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const allEvents: any[] = [];
      let pageToken: string | undefined = undefined;
      let pageCount = 0;
      setFetchedEventCount(null);
      setFetchingPage(0);

      do {
        setFetchingPage(pageCount + 1);
        const url = new URL(
          `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(selectedCalendarId)}/events`
        );
        url.searchParams.set('timeMin', timeMin.toISOString());
        url.searchParams.set('timeMax', timeMax.toISOString());
        url.searchParams.set('singleEvents', 'true');
        url.searchParams.set('orderBy', 'startTime');
        url.searchParams.set('maxResults', '250');
        if (pageToken) url.searchParams.set('pageToken', pageToken);

        const response = await fetch(url.toString(), {
          headers: { Authorization: `Bearer ${accessToken}` },
        });

        if (!response.ok) {
          if (response.status === 401) {
            setConnectionStatus('expired');
            alert('Din Google session er udløbet. Tilslut venligst igen.');
            return;
          }
          throw new Error(`Google Calendar API fejl: ${response.status}`);
        }

        const page = await response.json();
        allEvents.push(...(page.items ?? []));
        pageToken = page.nextPageToken;
        pageCount++;
        if (pageCount >= 20) break;
      } while (pageToken);

      setFetchedEventCount(allEvents.length);
      setFetchingPage(0);

      // Fetch existing Azure trips for the period to detect already-saved events
      const periodStart = timeMin.toLocaleDateString('en-CA');
      const periodEnd = timeMax.toLocaleDateString('en-CA');
      const result = await tripService.getTrips(undefined, 1, 100000);
      const existingTrips = result.data.filter((t) => t.date >= periodStart && t.date <= periodEnd);

      // Orphaned trips = in period but no googleEventId (imported before this feature)
      setOrphanedTripCount(existingTrips.filter((t) => !t.googleEventId).length);

      // Map googleEventId to saved trip data
      const savedByEventId = new Map<
        string,
        { id: string; startAddress: string; endAddress: string; distance: number }
      >();
      existingTrips.forEach((t) => {
        if (t.googleEventId)
          savedByEventId.set(t.googleEventId, {
            id: t.id,
            startAddress: t.startAddress,
            endAddress: t.endAddress,
            distance: t.distance,
          });
      });

      // Only timed events (not all-day)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const timedEvents: any[] = allEvents.filter((ev: any) => ev.start?.dateTime);
      if (timedEvents.length === 0) {
        alert('Ingen kalenderaftaler fundet i denne periode.');
        return;
      }

      // Build proposed trip list with chain logic: each day starts at homeAddress,
      // consecutive trips chain off the previous end location.
      const proposed: ProposedTrip[] = [];
      let currentDayStr = '';
      let previousEndLocation = homeAddress;

      const chronological = timedEvents.sort(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (a: any, b: any) =>
          new Date(a.start.dateTime).getTime() - new Date(b.start.dateTime).getTime()
      );

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      for (const ev of chronological) {
        const dateStr = (ev.start.dateTime as string).split('T')[0]!;

        if (dateStr !== currentDayStr) {
          currentDayStr = dateStr;
          previousEndLocation = homeAddress; // New day -> reset to home
        }

        const existingTrip = savedByEventId.get(ev.id as string);
        const rawLoc = ((ev.location as string | undefined) ?? '').trim();
        let startLoc = '';
        let endLoc = '';

        if (rawLoc.includes(' - ')) {
          const parts = rawLoc.split(' - ');
          startLoc = parts[0]?.trim() ?? '';
          endLoc = parts.slice(1).join(' - ').trim();
          previousEndLocation = endLoc || startLoc;
        } else if (rawLoc) {
          endLoc = rawLoc;
          startLoc = previousEndLocation;
          previousEndLocation = endLoc;
        } else if (existingTrip) {
          startLoc = existingTrip.startAddress;
          endLoc = existingTrip.endAddress;
          if (endLoc) previousEndLocation = endLoc;
        } else {
          startLoc = previousEndLocation;
          endLoc = '';
        }

        proposed.push({
          id: ev.id as string,
          ...(existingTrip ? { dbTripId: existingTrip.id } : {}),
          date: dateStr,
          dateTime: ev.start.dateTime as string,
          ...(ev.end?.dateTime ? { endDateTime: ev.end.dateTime as string } : {}),
          purpose: (ev.summary as string | undefined) ?? 'Korsel',
          startLocation: startLoc,
          endLocation: endLoc,
          distanceKm: existingTrip?.distance ?? null,
          status: 'pending',
          isAlreadySaved: !!existingTrip,
          columnOverride: startLoc && endLoc ? 'with' : 'without',
        });
      }

      // Auto-add return-home trip at end of each day if last stop != home
      const byDay: Record<string, ProposedTrip[]> = {};
      proposed.forEach((t) => {
        if (!byDay[t.date]) byDay[t.date] = [];
        byDay[t.date]!.push(t);
      });
      const normalizeAddr = (a: string) =>
        a
          .toLowerCase()
          .replace(/,?\s*denmark$/i, '')
          .trim();

      Object.entries(byDay).forEach(([date, dayTrips]) => {
        const lastTripWithEnd = [...dayTrips].reverse().find((t) => t.endLocation?.trim());
        const lastEnd = lastTripWithEnd?.endLocation?.trim();
        if (!lastEnd || normalizeAddr(lastEnd) === normalizeAddr(homeAddress)) return;

        const returnHomeId = `return-home-${date}`;
        const existingReturn = savedByEventId.get(returnHomeId);
        const homeDateTime =
          lastTripWithEnd?.endDateTime ?? lastTripWithEnd?.dateTime ?? `${date}T23:59:00`;

        proposed.push({
          id: returnHomeId,
          ...(existingReturn ? { dbTripId: existingReturn.id } : {}),
          date,
          dateTime: homeDateTime,
          purpose: 'Hjemkorsel',
          startLocation: lastEnd,
          endLocation: homeAddress,
          distanceKm: existingReturn?.distance ?? null,
          status: 'pending',
          isAlreadySaved: !!existingReturn,
          columnOverride: lastEnd && homeAddress ? 'with' : 'without',
        });
      });

      // Final chronological sort — return-home trips always last per day
      proposed.sort((a, b) => {
        if (a.date !== b.date)
          return new Date(a.date).getTime() - new Date(b.date).getTime();
        if (a.id.startsWith('return-home-')) return 1;
        if (b.id.startsWith('return-home-')) return -1;
        return new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime();
      });

      setProposedTrips(proposed);
      // Pre-select unsaved trips only
      setSelectedTrips(proposed.filter((t) => !t.isAlreadySaved).map((t) => t.id));
    } catch (error) {
      console.error('Error loading events:', error);
      alert('Der opstod en fejl ved indlaesning af kalenderaftaler.');
    } finally {
      setLoading(false);
    }
  };

  // ── Proposed trip management ──────────────────────────────────────────────────

  const updateProposedTrip = (id: string, field: 'startLocation' | 'endLocation', value: string) => {
    setProposedTrips((prev) =>
      prev.map((t) =>
        t.id === id ? { ...t, [field]: value, status: 'pending', errorMessage: '' } : t
      )
    );
  };

  const updateProposedTripKm = (id: string, value: string) => {
    const km = parseFloat(value.replace(',', '.'));
    setProposedTrips((prev) =>
      prev.map((t) => (t.id === id ? { ...t, distanceKm: isNaN(km) ? null : km } : t))
    );
  };

  const removeProposedTrip = (id: string) => {
    setProposedTrips((prev) => prev.filter((t) => t.id !== id));
  };

  const toggleSelectTrip = (id: string) => {
    setSelectedTrips((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
    );
  };

  const moveToColumn = (tripId: string, target: 'with' | 'without') => {
    setProposedTrips((prev) =>
      prev.map((t) => (t.id === tripId ? { ...t, columnOverride: target } : t))
    );
  };

  // ── Distance calculation (Routes API v2) ─────────────────────────────────────

  const getDistanceMeters = async (
    origin: string,
    destination: string,
    apiKey: string
  ): Promise<number> => {
    console.debug('[RouteMatrix] origin:', origin, '| destination:', destination);
    const response = await fetch(
      'https://routes.googleapis.com/distanceMatrix/v2:computeRouteMatrix',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': apiKey,
          'X-Goog-FieldMask': 'originIndex,destinationIndex,distanceMeters,condition,status',
        },
        body: JSON.stringify({
          origins: [{ waypoint: { address: origin } }],
          destinations: [{ waypoint: { address: destination } }],
          travelMode: 'DRIVE',
          routingPreference: 'TRAFFIC_UNAWARE',
        }),
      }
    );
    if (!response.ok) throw new Error(`Routes API ${response.status}`);
    const results = await response.json();
    if (!Array.isArray(results) || results.length === 0)
      throw new Error('Tomt svar fra Routes API');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result = results.find((r: any) => r.originIndex === 0 && r.destinationIndex === 0);
    if (!result) throw new Error('Ingen matching result');
    console.debug('[RouteMatrix] result:', JSON.stringify(result));
    if (result.condition === 'ROUTE_NOT_FOUND') throw new Error('ZERO_RESULTS');
    if (result.status?.code) throw new Error(`Routes API status ${result.status.code}: ${result.status.message}`);
    if (!result.distanceMeters)
      throw new Error(`Ingen distanceMeters — condition: ${result.condition}, status: ${JSON.stringify(result.status)}`);
    return result.distanceMeters as number;
  };

  /** Recalculate km for a single trip (state only, does NOT save to DB). */
  const calculateSingleTrip = async (tripId: string) => {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      alert('Google API key mangler');
      return;
    }
    const trip = proposedTrips.find((t) => t.id === tripId);
    if (!trip?.startLocation || !trip?.endLocation) return;

    setCalculatingTripId(tripId);
    setProposedTrips((prev) =>
      prev.map((t) => (t.id === tripId ? { ...t, status: 'calculating', errorMessage: '' } : t))
    );
    try {
      const meters = await getDistanceMeters(trip.startLocation, trip.endLocation, apiKey);
      const distanceKm = Math.round(meters / 1000);
      setProposedTrips((prev) =>
        prev.map((t) =>
          t.id === tripId ? { ...t, distanceKm, status: 'pending', errorMessage: '' } : t
        )
      );
    } catch (err: unknown) {
      const msg =
        err instanceof Error && err.message === 'ZERO_RESULTS'
          ? 'Ingen korerute fundet'
          : `Beregningsfejl: ${err instanceof Error ? err.message : 'Ukendt'}`;
      setProposedTrips((prev) =>
        prev.map((t) => (t.id === tripId ? { ...t, status: 'error', errorMessage: msg } : t))
      );
    } finally {
      setCalculatingTripId(null);
    }
  };

  /** Batch-calculate km for all selected trips with addresses (state only, no save). */
  const calculateAllDistances = async () => {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      alert('Google API key mangler');
      return;
    }
    const toCalc = proposedTrips.filter(
      (t) => selectedTrips.includes(t.id) && t.startLocation && t.endLocation
    );
    if (toCalc.length === 0) {
      alert('Vaelg ture med adresser der skal beregnes.');
      return;
    }

    setIsCalculating(true);
    let current = [...proposedTrips];

    for (const trip of toCalc) {
      const idx = current.findIndex((t) => t.id === trip.id);
      if (idx === -1) continue;
      current[idx] = { ...current[idx]!, status: 'calculating', errorMessage: '' };
      setProposedTrips([...current]);
      try {
        const meters = await getDistanceMeters(trip.startLocation, trip.endLocation, apiKey);
        const distanceKm = Math.round(meters / 1000);
        current[idx] = { ...current[idx]!, distanceKm, status: 'pending', errorMessage: '' };
      } catch (err: unknown) {
        const msg =
          err instanceof Error && err.message === 'ZERO_RESULTS'
            ? 'Ingen korerute fundet'
            : `Fejl: ${err instanceof Error ? err.message : 'Ukendt'}`;
        current[idx] = { ...current[idx]!, status: 'error', errorMessage: msg };
      }
      setProposedTrips([...current]);
      await new Promise((r) => setTimeout(r, 150));
    }
    setIsCalculating(false);
  };

  // ── Save trips ────────────────────────────────────────────────────────────────

  const buildTripPayload = (trip: ProposedTrip) => {
    const dt = new Date(trip.dateTime);
    const startTime = `${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`;
    return {
      startAddress: trip.startLocation,
      endAddress: trip.endLocation,
      distance: trip.distanceKm!,
      purpose: trip.purpose,
      date: trip.date,
      startTime,
      type: TripType.Business,
      googleEventId: trip.id,
    };
  };

  /** Save trips that already have km calculated (does NOT calculate first). */
  const saveCalculatedTrips = async () => {
    const toSave = proposedTrips.filter(
      (t) =>
        selectedTrips.includes(t.id) &&
        t.startLocation &&
        t.endLocation &&
        t.distanceKm !== null
    );
    if (toSave.length === 0) {
      alert('Ingen ture at gemme. Beregn km for de valgte ture forst.');
      return;
    }

    setIsSaving(true);
    let successCount = 0;
    let current = [...proposedTrips];

    for (const trip of toSave) {
      const idx = current.findIndex((t) => t.id === trip.id);
      if (idx === -1) continue;
      try {
        const payload = buildTripPayload(trip);
        if (trip.dbTripId) {
          await tripService.updateTrip(trip.dbTripId, payload);
        } else {
          await tripService.createTrip(payload);
        }
        current[idx] = { ...current[idx]!, status: 'success' };
        successCount++;
      } catch (err: unknown) {
        current[idx] = {
          ...current[idx]!,
          status: 'error',
          errorMessage: err instanceof Error ? err.message : 'Fejl ved gemning',
        };
      }
      setProposedTrips([...current]);
    }

    setProposedTrips(current.filter((t) => t.status !== 'success'));
    setIsSaving(false);
    if (successCount > 0) {
      alert(`${successCount} tur${successCount !== 1 ? 'e' : ''} gemt i korselsbogen!`);
      onImport();
    } else {
      alert('Ingen ture blev gemt.');
    }
  };

  /** Calculate km AND save to DB in one step. */
  const calculateAndSave = async () => {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      alert('Google Maps API key mangler i .env filen.');
      return;
    }

    const tripsToProcess = proposedTrips.filter(
      (t) =>
        selectedTrips.includes(t.id) &&
        t.startLocation &&
        t.endLocation &&
        t.status !== 'success'
    );
    const skipped = proposedTrips.filter(
      (t) => selectedTrips.includes(t.id) && (!t.startLocation || !t.endLocation)
    );

    if (tripsToProcess.length === 0) {
      alert(
        skipped.length > 0
          ? `De ${skipped.length} valgte ture mangler adresser. Udfyld adresserne i kolonnen til hojre forst.`
          : 'Vaelg venligst de ture du onsker at importere.'
      );
      return;
    }
    if (
      skipped.length > 0 &&
      !confirm(
        `${skipped.length} valgte tur(e) mangler adresser og springes over.\nFortsaet med at beregne ${tripsToProcess.length} ture?`
      )
    )
      return;

    setIsCalculating(true);
    let currentProposed = [...proposedTrips];
    let successCount = 0;

    try {
      for (const trip of tripsToProcess) {
        const idx = currentProposed.findIndex((t) => t.id === trip.id);
        if (idx === -1) continue;
        try {
          const meters = await getDistanceMeters(trip.startLocation, trip.endLocation, apiKey);
          const distanceKm = Math.round(meters / 1000);
          const payload = buildTripPayload({ ...trip, distanceKm });

          if (trip.dbTripId) {
            await tripService.updateTrip(trip.dbTripId, payload);
          } else {
            await tripService.createTrip(payload);
          }
          currentProposed[idx] = { ...currentProposed[idx]!, status: 'success', distanceKm };
          successCount++;
        } catch (err: unknown) {
          let errMsg = 'Beregningsfejl';
          if (err instanceof Error) {
            if (err.message === 'ZERO_RESULTS') errMsg = 'Ingen korerute fundet';
            else if (err.message.includes('403'))
              errMsg = 'API-adgang naegtet (403) - tjek at Routes API er aktiveret i Google Cloud Console';
            else if (err.message.includes('Routes API')) errMsg = `API-fejl: ${err.message}`;
          }
          currentProposed[idx] = { ...currentProposed[idx]!, status: 'error', errorMessage: errMsg };
        }
        setProposedTrips([...currentProposed]);
        await new Promise((r) => setTimeout(r, 200));
      }

      setProposedTrips(currentProposed.filter((t) => t.status !== 'success'));
      if (successCount > 0) {
        alert(`${successCount} tur${successCount !== 1 ? 'e' : ''} blev beregnet og gemt!`);
        onImport();
      } else {
        alert('Ingen ture blev gemt. Tjek adresserne og prov igen.');
      }
    } catch (err) {
      console.error(err);
      alert('Der opstod en kritisk fejl under beregning.');
    } finally {
      setIsCalculating(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-4xl mx-auto">
      {/* Home address warning */}
      {!homeAddress && (
        <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 mb-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-900">
              Hjemmeadresse mangler - import er deaktiveret
            </p>
            <p className="text-sm text-red-700 mt-1">
              Ga til <strong>Indstillinger</strong> og angiv din hjemmeadresse. Den bruges som
              startpunkt for den forste tur hver dag, og naeste tur bruger automatisk slutadressen
              fra den forrige.
            </p>
          </div>
        </div>
      )}

      {!isConnected ? (
        /* ── Not connected ── */
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <Calendar className="w-8 h-8 text-blue-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Tilslut Google Kalender</h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Forbind din Google Kalender for nemt at hente og beregne dine aftaler til korselsbogen.
          </p>
          <button
            onClick={handleConnectCalendar}
            className="inline-flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <LinkIcon className="w-5 h-5" />
            <span>Tilslut Google Kalender</span>
          </button>
        </div>
      ) : connectionStatus === 'expired' && proposedTrips.length === 0 ? (
        /* ── Token expired + nothing to show ── */
        <div className="text-center py-12 bg-white rounded-xl border border-amber-200 shadow-sm">
          <div className="bg-amber-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <WifiOff className="w-8 h-8 text-amber-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Google-session udlobet</h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Din forbindelse til Google Kalender er udlobet (tokens er gyldige i 1 time). Tilslut
            igen for at hente nye aftaler.
          </p>
          <button
            onClick={handleConnectCalendar}
            className="inline-flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <LinkIcon className="w-5 h-5" />
            <span>Tilslut igen</span>
          </button>
        </div>
      ) : (
        /* ── Connected ── */
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            {/* Connection status row */}
            <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                {connectionStatus === 'checking' && (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                    <span className="text-sm text-blue-600 font-medium">
                      Kontrollerer forbindelse...
                    </span>
                  </>
                )}
                {connectionStatus === 'valid' && (
                  <>
                    <Wifi className="w-4 h-4 text-green-500" />
                    <span className="text-sm text-green-600 font-medium">
                      Kalender forbundet og aktiv
                    </span>
                    {lastChecked && (
                      <span className="text-xs text-gray-400 ml-1">
                        {'·'} kontrolleret{' '}
                        {lastChecked.toLocaleTimeString('da-DK', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    )}
                  </>
                )}
                {connectionStatus === 'expired' && (
                  <>
                    <WifiOff className="w-4 h-4 text-amber-500" />
                    <span className="text-sm text-amber-600 font-medium">
                      Session udlobet — tilslut igen
                    </span>
                  </>
                )}
                {connectionStatus === 'disconnected' && (
                  <>
                    <WifiOff className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-500">Ikke forbundet</span>
                  </>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => validateToken(accessToken)}
                  disabled={connectionStatus === 'checking'}
                  title="Tjek forbindelse nu"
                  className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  <RefreshCw
                    className={`w-3.5 h-3.5 ${connectionStatus === 'checking' ? 'animate-spin' : ''}`}
                  />
                </button>
                {connectionStatus === 'expired' ? (
                  <button
                    onClick={handleConnectCalendar}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Tilslut igen
                  </button>
                ) : (
                  <button
                    onClick={handleDisconnectCalendar}
                    className="text-sm text-red-500 hover:text-red-700"
                  >
                    Afbryd
                  </button>
                )}
              </div>
            </div>

            {/* Period selector + fetch button */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {fetchMode === 'year'
                    ? `Hent hele ${selectedYear}`
                    : `Hent aftaler fra ${MONTH_NAMES[selectedMonth]} ${selectedYear}`}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Systemet foreslår ruter udfra din hjemmeadresse og kalenderaftaler.
                </p>
                <div className="mt-3 space-y-1">
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Vaelg Google-kalender
                  </label>
                  <select
                    value={selectedCalendarId}
                    onChange={(e) => handleCalendarChange(e.target.value)}
                    disabled={loadingCalendars || connectionStatus !== 'valid' || availableCalendars.length === 0}
                    className="w-full sm:w-80 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-400"
                  >
                    {availableCalendars.length === 0 ? (
                      <option value="">Ingen ejede kalendere fundet</option>
                    ) : (
                      availableCalendars.map((calendar) => (
                        <option key={calendar.id} value={calendar.id}>
                          {calendar.summary}{calendar.primary ? ' (Primar)' : ''}
                        </option>
                      ))
                    )}
                  </select>
                  {loadingCalendars && (
                    <p className="text-xs text-gray-500">Henter kalendere...</p>
                  )}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                {/* Month / Year toggle */}
                <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm font-medium">
                  <button
                    onClick={() => setFetchMode('month')}
                    className={`px-3 py-2 transition-colors ${
                      fetchMode === 'month'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    Maaned
                  </button>
                  <button
                    onClick={() => setFetchMode('year')}
                    className={`px-3 py-2 transition-colors border-l border-gray-200 ${
                      fetchMode === 'year'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    Hele ar
                  </button>
                </div>
                <button
                  onClick={loadCalendarEvents}
                  disabled={
                    loading ||
                    connectionStatus === 'expired' ||
                    loadingCalendars ||
                    !selectedCalendarId
                  }
                  className="flex items-center justify-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors font-medium whitespace-nowrap"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  <span>
                    {loading
                      ? fetchingPage > 1
                        ? `Henter side ${fetchingPage}...`
                        : 'Henter...'
                      : fetchMode === 'year'
                        ? `Hent hele ${selectedYear}`
                        : 'Hent maaned'}
                  </span>
                </button>
                {fetchedEventCount !== null && !loading && (
                  <span className="text-xs text-gray-500 whitespace-nowrap">
                    {fetchedEventCount} aftaler hentet
                  </span>
                )}
              </div>
            </div>

            {/* Delete section */}
            <div className="mt-3 pt-3 border-t border-gray-100">
              {(() => {
                const periodLabel =
                  fetchMode === 'year'
                    ? `hele ${selectedYear}`
                    : `${MONTH_NAMES[selectedMonth]} ${selectedYear}`;
                return (
                  <>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                      Slet ture for {periodLabel}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-2 flex-wrap">
                      <button
                        onClick={deleteCalendarTripsForPeriod}
                        disabled={isDeleting}
                        className="flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Slet kalenderimporterede ({periodLabel})
                      </button>
                      <button
                        onClick={deleteManualTripsForPeriod}
                        disabled={isDeleting}
                        className="flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 disabled:opacity-50 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Slet manuelle ({periodLabel})
                      </button>
                      <button
                        onClick={deleteAllTripsForPeriod}
                        disabled={isDeleting}
                        className="flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-red-600 border border-red-700 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Nulstil alt ({periodLabel})
                      </button>
                    </div>
                  </>
                );
              })()}

              {orphanedTripCount > 0 && (
                <div className="mt-3 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-amber-800">
                      {orphanedTripCount} foraeldre-lose ture fundet
                    </p>
                    <p className="text-xs text-amber-700 mt-0.5">
                      Der er {orphanedTripCount} ture i perioden uden kalender-ID — sandsynligvis
                      fra et gammelt import. Klik <strong>Nulstil hele perioden</strong> for at
                      rydde dem.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Proposed trips columns */}
          {proposedTrips.length > 0 &&
            (() => {
              const withLoc = proposedTrips.filter(
                (t) =>
                  t.columnOverride === 'with' ||
                  (t.columnOverride !== 'without' && t.startLocation && t.endLocation)
              );
              const withoutLoc = proposedTrips.filter(
                (t) =>
                  t.columnOverride === 'without' ||
                  (t.columnOverride !== 'with' && (!t.startLocation || !t.endLocation))
              );

              const groupByDate = (trips: ProposedTrip[]) => {
                const grouped = trips.reduce(
                  (acc, trip) => {
                    if (!acc[trip.date]) acc[trip.date] = [];
                    acc[trip.date]!.push(trip);
                    return acc;
                  },
                  {} as Record<string, ProposedTrip[]>
                );
                return {
                  grouped,
                  sortedDates: Object.keys(grouped).sort(
                    (a, b) => new Date(a).getTime() - new Date(b).getTime()
                  ),
                };
              };

              const renderTripCard = (trip: ProposedTrip, currentColumn: 'with' | 'without') => (
                <div
                  key={trip.id}
                  className={`p-3 my-1 rounded-lg flex items-start space-x-3 transition-colors border ${
                    trip.isAlreadySaved
                      ? 'border-green-200 bg-green-50'
                      : 'border-transparent hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedTrips.includes(trip.id)}
                    onChange={() => toggleSelectTrip(trip.id)}
                    className="mt-1.5 w-4 h-4 text-blue-600 rounded border-gray-300 flex-shrink-0 cursor-pointer"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start mb-2">
                      <div className="min-w-0 pr-2">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <h4 className="text-xs font-semibold text-gray-900 truncate">
                            {trip.purpose}
                          </h4>
                          {trip.isAlreadySaved && (
                            <span className="flex-shrink-0 text-xs font-medium text-green-700 bg-green-100 px-1.5 py-0.5 rounded-full">
                              Gemt
                            </span>
                          )}
                          {trip.id.startsWith('return-home-') && (
                            <span className="flex-shrink-0 text-xs font-medium text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded-full">
                              Hjemkorsel
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-400">
                          {new Date(trip.dateTime).toLocaleTimeString('da-DK', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                          {trip.endDateTime &&
                            ` – ${new Date(trip.endDateTime).toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' })}`}
                        </p>
                      </div>
                      <button
                        onClick={() => removeProposedTrip(trip.id)}
                        className="p-1 text-gray-300 hover:text-red-500 flex-shrink-0"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    {/* Address autocomplete inputs */}
                    <div className="space-y-1.5">
                      {(['startLocation', 'endLocation'] as const).map((field) => (
                        <AddressAutocomplete
                          key={field}
                          compact
                          value={trip[field]}
                          onChange={(val) => updateProposedTrip(trip.id, field, val)}
                          placeholder={
                            field === 'startLocation'
                              ? 'Mangler startadresse...'
                              : 'Mangler slutadresse...'
                          }
                        />
                      ))}
                    </div>

                    {/* Distance row */}
                    <div className="mt-2 flex items-center gap-2">
                      {trip.distanceKm !== null ? (
                        <div className="flex items-center gap-1.5 flex-1">
                          <input
                            type="number"
                            min="0"
                            step="0.1"
                            value={trip.distanceKm}
                            onChange={(e) => updateProposedTripKm(trip.id, e.target.value)}
                            className="w-20 px-2 py-0.5 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-blue-500 text-gray-700 font-medium"
                          />
                          <span className="text-xs text-gray-400">km</span>
                          <span className="text-xs text-gray-400 italic">ret km ved anden rute</span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 italic flex-1">
                          {trip.status === 'calculating' ? 'Beregner...' : 'Ikke beregnet'}
                        </span>
                      )}
                      {trip.startLocation && trip.endLocation && (
                        <>
                          <button
                            onClick={() => calculateSingleTrip(trip.id)}
                            disabled={calculatingTripId === trip.id || isCalculating}
                            title="Genberegn km for denne tur"
                            className="flex-shrink-0 p-1 text-gray-400 hover:text-green-600 transition-colors rounded disabled:opacity-40"
                          >
                            <RotateCcw
                              className={`w-3.5 h-3.5 ${calculatingTripId === trip.id ? 'animate-spin' : ''}`}
                            />
                          </button>
                          <a
                            href={`https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(trip.startLocation)}&destination=${encodeURIComponent(trip.endLocation)}&travelmode=driving`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Abn rute i Google Maps"
                            className="flex-shrink-0 p-1 text-gray-400 hover:text-blue-600 transition-colors rounded"
                          >
                            <MapIcon className="w-3.5 h-3.5" />
                          </a>
                        </>
                      )}
                    </div>

                    {/* Move to other column */}
                    <div className="mt-1.5">
                      <button
                        onClick={() =>
                          moveToColumn(trip.id, currentColumn === 'with' ? 'without' : 'with')
                        }
                        className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-700 transition-colors"
                      >
                        <ArrowRightLeft className="w-3 h-3" />
                        {currentColumn === 'with' ? 'Flyt til Mangler adresse' : 'Flyt til Med lokation'}
                      </button>
                    </div>

                    {/* Missing address hint */}
                    {(!trip.startLocation || !trip.endLocation) && (
                      <div className="mt-2 flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                        <AlertCircle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                        <p className="text-xs text-amber-700 flex-1">
                          Tilfoej lokation i Google Kalender og synkroniser
                        </p>
                        <a
                          href={(() => {
                            const [y, m, d] = trip.date.split('-');
                            return `https://calendar.google.com/calendar/r/day/${y}/${parseInt(m!)}/${parseInt(d!)}`;
                          })()}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-800 bg-white border border-blue-200 px-2 py-1 rounded-lg whitespace-nowrap transition-colors"
                        >
                          <Calendar className="w-3 h-3" />
                          Abn kalender
                        </a>
                      </div>
                    )}

                    {/* Error message */}
                    {trip.status === 'error' && trip.errorMessage && (
                      <p className="mt-1 text-xs text-red-600 bg-red-50 p-1.5 rounded">
                        {trip.errorMessage}
                      </p>
                    )}
                  </div>
                </div>
              );

              const renderColumn = (
                trips: ProposedTrip[],
                title: string,
                colorClass: string,
                onSelectAll: () => void,
                allSelected: boolean,
                showAddButton: boolean,
                showSyncButton: boolean,
                columnType: 'with' | 'without',
                collapsed: boolean,
                onToggleCollapse: () => void
              ) => {
                const { grouped, sortedDates } = groupByDate(trips);
                const selectedCount = trips.filter((t) => selectedTrips.includes(t.id)).length;
                return (
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col">
                    <div
                      className={`p-3 border-b border-gray-200 flex justify-between items-center gap-2 ${colorClass}`}
                    >
                      <button
                        onClick={onToggleCollapse}
                        className="flex items-center gap-2 flex-1 min-w-0 text-left"
                      >
                        {collapsed
                          ? <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
                          : <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />}
                        <span className="text-sm font-semibold text-gray-900">{title}</span>
                        <span className="text-xs text-gray-500 bg-white/60 px-1.5 py-0.5 rounded-full">{trips.length}</span>
                        {selectedCount > 0 && (
                          <span className="text-xs text-[#5779A2] bg-[#EEF1F6] px-1.5 py-0.5 rounded-full">{selectedCount} valgt</span>
                        )}
                      </button>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {showAddButton && (
                          <button
                            onClick={() => setShowNewEventForm(true)}
                            className="flex items-center gap-1 text-xs font-medium text-white bg-[#1B2C45] hover:bg-[#243a5e] px-2.5 py-1.5 rounded-lg transition-colors"
                          >
                            <Plus className="w-3.5 h-3.5" />
                            Ny aftale
                          </button>
                        )}
                        {showSyncButton && (
                          <button
                            onClick={loadCalendarEvents}
                            disabled={loading}
                            className="flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-100 hover:bg-amber-200 border border-amber-300 px-2.5 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                          >
                            <RefreshCw
                              className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`}
                            />
                            Synkroniser
                          </button>
                        )}
                        {!collapsed && (
                          <button
                            onClick={onSelectAll}
                            className="text-xs font-medium text-[#5779A2] hover:text-[#445f85] whitespace-nowrap"
                          >
                            {allSelected ? 'Fravælg alle' : 'Vælg alle'}
                          </button>
                        )}
                      </div>
                    </div>
                    {!collapsed && (
                      <div className="overflow-y-auto max-h-[70vh] p-2">
                      {sortedDates.length === 0 ? (
                        <p className="text-xs text-gray-400 text-center py-8">Ingen aftaler</p>
                      ) : (
                        sortedDates.map((date) => (
                          <div key={date} className="mb-4">
                            <p className="text-xs font-bold text-gray-700 uppercase tracking-wide px-1 pt-1 pb-1.5 border-b border-gray-100">
                              {new Date(`${date}T12:00:00`).toLocaleDateString('da-DK', {
                                weekday: 'long',
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                              })}
                            </p>
                            <div className="space-y-1">
                              {grouped[date]!.map((trip) => {
                                const endDate = trip.endDateTime
                                  ? new Date(trip.endDateTime)
                                  : null;
                                const endTimeStr = endDate
                                  ? `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`
                                  : '';
                                const computedEndTime = endDate
                                  ? `${String((endDate.getHours() + 1) % 24).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`
                                  : '';
                                return (
                                  <div key={trip.id}>
                                    {renderTripCard(trip, columnType)}
                                    {/* Insert new event after this trip */}
                                    <div className="flex justify-center py-0.5">
                                      <button
                                        onClick={() => {
                                          setNewEvent((p) => ({
                                            ...p,
                                            date: trip.date,
                                            startTime: endTimeStr || p.startTime,
                                            endTime: computedEndTime || p.endTime,
                                          }));
                                          setShowNewEventForm(true);
                                        }}
                                        title="Tilfoej ny kalenderaftale efter denne"
                                        className="text-gray-300 hover:text-[#5779A2] transition-colors"
                                      >
                                        <Plus className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    )}
                  </div>
                );
              };

              const withLocIds = withLoc.map((t) => t.id);
              const withoutLocIds = withoutLoc.map((t) => t.id);

              return (
                <div className="flex flex-col gap-4">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {renderColumn(
                      withLoc,
                      'Med lokation',
                      'bg-gray-50',
                      () => {
                        const allSel = withLocIds.every((id) => selectedTrips.includes(id));
                        setSelectedTrips((prev) =>
                          allSel
                            ? prev.filter((id) => !withLocIds.includes(id))
                            : [...new Set([...prev, ...withLocIds])]
                        );
                      },
                      withLocIds.length > 0 && withLocIds.every((id) => selectedTrips.includes(id)),
                      true,
                      false,
                      'with',
                      withLocCollapsed,
                      () => setWithLocCollapsed(p => !p)
                    )}
                    {renderColumn(
                      withoutLoc,
                      'Mangler adresse',
                      'bg-amber-50',
                      () => {
                        const allSel =
                          withoutLocIds.length > 0 &&
                          withoutLocIds.every((id) => selectedTrips.includes(id));
                        setSelectedTrips((prev) =>
                          allSel
                            ? prev.filter((id) => !withoutLocIds.includes(id))
                            : [...new Set([...prev, ...withoutLocIds])]
                        );
                      },
                      withoutLocIds.length > 0 &&
                        withoutLocIds.every((id) => selectedTrips.includes(id)),
                      false,
                      true,
                      'without',
                      withoutLocCollapsed,
                      () => setWithoutLocCollapsed(p => !p)
                    )}
                  </div>

                  {/* Action bar */}
                  <div className="pt-3 border-t border-gray-100">
                    {(() => {
                      const sel = proposedTrips.filter((t) => selectedTrips.includes(t.id));
                      const withAddr = sel.filter((t) => t.startLocation && t.endLocation);
                      const calculated = withAddr.filter((t) => t.distanceKm !== null);
                      const notCalc = withAddr.filter((t) => t.distanceKm === null);
                      const nNew = sel.filter(
                        (t) =>
                          !t.isAlreadySaved &&
                          t.distanceKm !== null &&
                          t.startLocation &&
                          t.endLocation
                      ).length;
                      const nUpd = sel.filter(
                        (t) =>
                          t.isAlreadySaved &&
                          t.distanceKm !== null &&
                          t.startLocation &&
                          t.endLocation
                      ).length;
                      return (
                        <div className="mb-2 px-1 text-xs text-gray-500 flex flex-wrap gap-3">
                          <span>
                            <strong className="text-gray-700">{sel.length}</strong> valgt i alt
                          </span>
                          {withAddr.length > 0 && (
                            <span>
                              <strong className="text-gray-700">{withAddr.length}</strong> med
                              adresse
                            </span>
                          )}
                          {calculated.length > 0 && (
                            <span className="text-green-700">
                              <strong>{calculated.length}</strong> km beregnet
                            </span>
                          )}
                          {notCalc.length > 0 && (
                            <span className="text-amber-600">
                              <strong>{notCalc.length}</strong> mangler km-beregning
                            </span>
                          )}
                          {nNew > 0 && (
                            <span className="text-blue-700">
                              <strong>{nNew}</strong> nye klar til gem
                            </span>
                          )}
                          {nUpd > 0 && (
                            <span className="text-purple-700">
                              <strong>{nUpd}</strong> eksisterende klar til opdatering
                            </span>
                          )}
                        </div>
                      );
                    })()}
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={calculateAllDistances}
                        disabled={isCalculating || isSaving || selectedTrips.length === 0}
                        title="Beregner km for alle valgte ture med adresser — gemmer ikke endnu"
                        className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-all font-semibold shadow-sm text-sm"
                      >
                        <Calculator className="w-4 h-4" />
                        {isCalculating ? 'Beregner km...' : 'Beregn km for valgte'}
                      </button>
                      <button
                        onClick={saveCalculatedTrips}
                        disabled={isCalculating || isSaving || selectedTrips.length === 0}
                        title="Gemmer alle valgte ture der allerede har km beregnet"
                        className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-50 transition-all font-semibold shadow-sm text-sm"
                      >
                        <Save className="w-4 h-4" />
                        {isSaving
                          ? 'Gemmer...'
                          : (() => {
                              const ready = proposedTrips.filter(
                                (t) =>
                                  selectedTrips.includes(t.id) &&
                                  t.distanceKm !== null &&
                                  t.startLocation &&
                                  t.endLocation
                              );
                              return ready.length > 0
                                ? `Gem ${ready.length} tur${ready.length !== 1 ? 'e' : ''} i korselsbogen`
                                : 'Gem i korselsbogen';
                            })()}
                      </button>
                      <button
                        onClick={calculateAndSave}
                        disabled={isCalculating || isSaving || selectedTrips.length === 0}
                        title="Beregner km og gemmer i korselsbogen i et skridt"
                        className="flex items-center gap-2 px-4 py-2.5 bg-gray-700 text-white rounded-xl hover:bg-gray-800 disabled:opacity-50 transition-all font-medium shadow-sm text-sm"
                      >
                        <CheckCircle className="w-4 h-4" />
                        {isCalculating ? 'Beregner og gemmer...' : 'Beregn og gem i et skridt'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}
        </div>
      )}

      {/* Modal: Create new Google Calendar event */}
      {showNewEventForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-y-auto max-h-[90vh]">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                Opret ny kalenderaftale
              </h2>
              <button
                onClick={() => setShowNewEventForm(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Titel / formål
                </label>
                <input
                  type="text"
                  value={newEvent.summary}
                  onChange={(e) => setNewEvent((p) => ({ ...p, summary: e.target.value }))}
                  placeholder="F.eks. Kundemøde, Ejendomsfotografering..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Lokation / adresse
                </label>
                <AddressAutocomplete
                  value={newEvent.location}
                  onChange={(val) => setNewEvent((p) => ({ ...p, location: val }))}
                  placeholder="Søg adresse..."
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-1">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Dato</label>
                  <input
                    type="date"
                    value={newEvent.date}
                    onChange={(e) => setNewEvent((p) => ({ ...p, date: e.target.value }))}
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Fra</label>
                  <input
                    type="time"
                    value={newEvent.startTime}
                    onChange={(e) => setNewEvent((p) => ({ ...p, startTime: e.target.value }))}
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Til</label>
                  <input
                    type="time"
                    value={newEvent.endTime}
                    onChange={(e) => setNewEvent((p) => ({ ...p, endTime: e.target.value }))}
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Kørseltype</label>
                <div className="flex gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={newEvent.isBusiness}
                      onChange={() => setNewEvent((p) => ({ ...p, isBusiness: true }))}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-sm text-gray-700">Erhvervskørsel</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={!newEvent.isBusiness}
                      onChange={() => setNewEvent((p) => ({ ...p, isBusiness: false }))}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-sm text-gray-700">Privatkørsel</span>
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Beskrivelse (valgfri)
                </label>
                <textarea
                  value={newEvent.description}
                  onChange={(e) => setNewEvent((p) => ({ ...p, description: e.target.value }))}
                  rows={2}
                  placeholder="Yderligere noter..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                />
              </div>
            </div>
            <div className="px-6 pb-6 flex gap-3">
              <button
                onClick={() => setShowNewEventForm(false)}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
              >
                Annuller
              </button>
              <button
                onClick={createCalendarEvent}
                disabled={savingEvent}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm font-semibold"
              >
                {savingEvent ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {savingEvent ? 'Opretter...' : 'Opret kalenderaftale'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
